/* ==========================================================================
   LAMPE & FORM — Scroll-Story-Controller

   Steuert eine Bildsequenz über den normalen Scrollfortschritt.

   Grundsätze:
   - Der Nutzer scrollt wie überall sonst. Rad und Touch werden nie abgefangen.
   - Genau eine requestAnimationFrame-Schleife, und die läuft nur, solange die
     Szene im Blick ist.
   - Ein Frame wird nur gezeichnet, wenn sich der Zielindex tatsächlich ändert.
   - Kann oder soll die Sequenz nicht laufen (Sparmodus, langsame Verbindung,
     reduzierte Bewegung, Ladefehler), übernimmt der statische Zustand.
   ========================================================================== */
(function () {
  'use strict';

  var instances = [];

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Sparmodus, langsame Verbindung oder sehr schwaches Gerät:
     dann wird gar keine Sequenz geladen. */
  function shouldSkipSequence() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (c) {
      if (c.saveData) return true;
      if (typeof c.effectiveType === 'string' && /(^|-)2g$/.test(c.effectiveType)) return true;
    }
    if (typeof navigator.deviceMemory === 'number' && navigator.deviceMemory > 0 && navigator.deviceMemory < 2) return true;
    if (typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency > 0 && navigator.hardwareConcurrency < 3) return true;
    return false;
  }

  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

  function Story(root) {
    this.root = root;
    this.track = root.querySelector('[data-story-track]');
    this.stage = root.querySelector('[data-story-stage]');
    this.canvas = root.querySelector('[data-story-canvas]');
    this.poster = root.querySelector('[data-story-poster]');
    this.still = root.querySelector('[data-story-still]');
    this.progress = root.querySelector('[data-story-progress]');
    this.beats = Array.prototype.slice.call(root.querySelectorAll('[data-story-beat]'));

    this.frames = [];
    this.images = [];
    this.loadState = [];
    this.current = -1;
    this.target = 0;
    this.rafId = 0;
    this.active = false;
    this.ready = false;
    this.destroyed = false;
    this.ctx = null;

    this.onScroll = this.onScroll.bind(this);
    this.tick = this.tick.bind(this);

    this.init();
  }

  Story.prototype.goStatic = function (reason) {
    this.root.setAttribute('data-static', 'true');
    this.root.setAttribute('data-static-reason', reason);
    if (this.still) this.still.setAttribute('data-shown', 'true');
    this.stopLoop();
  };

  Story.prototype.init = function () {
    if (prefersReducedMotion()) { this.goStatic('reduced-motion'); return; }
    if (shouldSkipSequence()) { this.goStatic('save-data'); return; }
    if (!this.canvas || !this.track || !this.stage) { this.goStatic('markup'); return; }

    // Hochformat bekommt eine eigenständig komponierte Sequenz, kein
    // beschnittenes Querformat. Die Wahl fällt einmal beim Aufsetzen.
    this.portrait = window.matchMedia('(max-width: 749px)').matches;
    var attr = this.portrait ? 'data-frames-mobile' : 'data-frames-desktop';
    var raw = this.root.getAttribute(attr) || '';
    this.frames = raw.split('|').filter(Boolean);

    // Fehlt die Hochformat-Sequenz, lieber das Querformat als gar nichts.
    if (this.frames.length < 2 && this.portrait) {
      raw = this.root.getAttribute('data-frames-desktop') || '';
      this.frames = raw.split('|').filter(Boolean);
    }
    if (this.frames.length < 2) { this.goStatic('no-frames'); return; }

    this.prefetchRadius = parseInt(this.root.getAttribute('data-prefetch') || '4', 10);
    this.images = new Array(this.frames.length);
    this.loadState = new Array(this.frames.length).fill(0); // 0 offen, 1 lädt, 2 da, 3 Fehler
    this.failures = 0;

    try { this.ctx = this.canvas.getContext('2d', { alpha: false }); }
    catch (e) { this.goStatic('no-2d'); return; }
    if (!this.ctx) { this.goStatic('no-2d'); return; }

    this.resize();

    // Die Schleife läuft nur, solange die Szene in Viewport-Nähe ist.
    var self = this;
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) self.activate(); else self.deactivate();
        });
      }, { rootMargin: '200px 0px 200px 0px' });
      this.io.observe(this.track);
    } else {
      this.activate();
    }

    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(function () { self.resize(); self.current = -1; self.draw(); });
      this.ro.observe(this.stage);
    } else {
      this.onResize = function () { self.resize(); self.current = -1; self.draw(); };
      window.addEventListener('resize', this.onResize);
    }

    // Wechselt das Format (Drehung, Fenstergröße), wird mit der passenden
    // Sequenz neu aufgesetzt statt die falsche weiterzuzeichnen.
    this.mq = window.matchMedia('(max-width: 749px)');
    this.onFormat = function () {
      if (self.destroyed || self.mq.matches === self.portrait) return;
      var host = self.root;
      self.destroy();
      host.__lafStory = null;
      mount(host.parentNode || document);
    };
    if (this.mq.addEventListener) this.mq.addEventListener('change', this.onFormat);
    else if (this.mq.addListener) this.mq.addListener(this.onFormat);
  };

  Story.prototype.activate = function () {
    if (this.active || this.destroyed) return;
    this.active = true;
    // Passiv: der Listener darf das Scrollen niemals verzögern.
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.loadAround(0);
    this.onScroll();
    this.startLoop();
  };

  Story.prototype.deactivate = function () {
    if (!this.active) return;
    this.active = false;
    window.removeEventListener('scroll', this.onScroll);
    this.stopLoop();
  };

  Story.prototype.startLoop = function () {
    if (this.rafId || this.destroyed) return;
    this.rafId = window.requestAnimationFrame(this.tick);
  };

  Story.prototype.stopLoop = function () {
    if (!this.rafId) return;
    window.cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  };

  Story.prototype.resize = function () {
    var rect = this.stage.getBoundingClientRect();
    var dpr = Math.min(window.devicePixelRatio || 1, 2); // DPR-Deckel laut Vorgabe
    var w = Math.max(1, Math.round(rect.width * dpr));
    var h = Math.max(1, Math.round(rect.height * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
  };

  /* Fortschritt 0..1 über die gesamte Scrollstrecke der Szene. */
  Story.prototype.computeProgress = function () {
    var rect = this.track.getBoundingClientRect();
    var total = rect.height - window.innerHeight;
    if (total <= 0) return 0;
    return clamp(-rect.top / total, 0, 1);
  };

  Story.prototype.onScroll = function () {
    var p = this.computeProgress();
    this.progressValue = p;
    var idx = Math.round(p * (this.frames.length - 1));
    if (idx !== this.target) {
      this.target = idx;
      this.loadAround(idx);
    }
    this.updateBeats(p);
    if (this.progress) this.progress.style.setProperty('--p', p.toFixed(4));
    this.startLoop();
  };

  Story.prototype.updateBeats = function (p) {
    if (!this.beats.length) return;
    for (var i = 0; i < this.beats.length; i++) {
      var el = this.beats[i];
      var from = parseFloat(el.getAttribute('data-from') || '0');
      var to = parseFloat(el.getAttribute('data-to') || '1');
      var on = p >= from && p < to;
      var was = el.getAttribute('data-active') === 'true';
      if (on !== was) el.setAttribute('data-active', on ? 'true' : 'false');
    }
  };

  /* Zuerst der aktuelle Frame, dann der Ring drumherum, den Rest im Hintergrund. */
  Story.prototype.loadAround = function (idx) {
    var n = this.frames.length;
    var r = this.prefetchRadius;
    this.loadFrame(idx);
    for (var d = 1; d <= r; d++) {
      if (idx + d < n) this.loadFrame(idx + d);
      if (idx - d >= 0) this.loadFrame(idx - d);
    }
    if (!this.idleQueued) {
      this.idleQueued = true;
      var self = this;
      var rest = function () {
        for (var i = 0; i < n; i++) self.loadFrame(i);
      };
      if ('requestIdleCallback' in window) window.requestIdleCallback(rest, { timeout: 4000 });
      else window.setTimeout(rest, 1500);
    }
  };

  Story.prototype.loadFrame = function (i) {
    if (this.destroyed) return;
    if (this.loadState[i] !== 0) return;
    this.loadState[i] = 1;
    var self = this;
    var img = new Image();
    img.decoding = 'async';
    img.onload = function () {
      self.loadState[i] = 2;
      self.images[i] = img;
      if (!self.ready) { self.ready = true; self.canvas.setAttribute('data-ready', 'true'); }
      if (i === self.target) self.startLoop();
    };
    img.onerror = function () {
      self.loadState[i] = 3;
      self.failures++;
      // Wenn ein nennenswerter Teil der Sequenz fehlt, ist sie nicht brauchbar.
      if (self.failures > Math.max(2, Math.floor(self.frames.length * 0.2))) {
        self.goStatic('load-error');
      }
    };
    img.src = this.frames[i];
  };

  /* Nächstgelegener bereits geladener Frame — verhindert Leerstellen,
     solange die Sequenz noch nachlädt. */
  Story.prototype.resolveIndex = function (idx) {
    if (this.loadState[idx] === 2) return idx;
    var n = this.frames.length;
    for (var d = 1; d < n; d++) {
      if (idx - d >= 0 && this.loadState[idx - d] === 2) return idx - d;
      if (idx + d < n && this.loadState[idx + d] === 2) return idx + d;
    }
    return -1;
  };

  Story.prototype.draw = function () {
    var idx = this.resolveIndex(this.target);
    if (idx < 0 || idx === this.current) return false;
    var img = this.images[idx];
    if (!img) return false;

    var cw = this.canvas.width;
    var ch = this.canvas.height;
    var iw = img.naturalWidth || img.width;
    var ih = img.naturalHeight || img.height;
    if (!iw || !ih) return false;

    // object-fit: cover von Hand
    var scale = Math.max(cw / iw, ch / ih);
    var dw = iw * scale;
    var dh = ih * scale;
    var dx = (cw - dw) / 2;
    var dy = (ch - dh) / 2;

    this.ctx.drawImage(img, dx, dy, dw, dh);
    this.current = idx;
    return true;
  };

  Story.prototype.tick = function () {
    this.rafId = 0;
    if (this.destroyed || !this.active) return;
    var drew = this.draw();
    // Nur weiterlaufen, wenn noch etwas zu tun ist: entweder der Zielframe
    // ist noch nicht da, oder gerade wurde gezeichnet.
    if (drew || this.current !== this.target) this.startLoop();
  };

  Story.prototype.destroy = function () {
    this.destroyed = true;
    this.deactivate();
    if (this.io) { this.io.disconnect(); this.io = null; }
    if (this.ro) { this.ro.disconnect(); this.ro = null; }
    if (this.onResize) { window.removeEventListener('resize', this.onResize); this.onResize = null; }
    if (this.mq && this.onFormat) {
      if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.onFormat);
      else if (this.mq.removeListener) this.mq.removeListener(this.onFormat);
      this.onFormat = null;
    }
    this.images = [];
  };

  /* ------------------------------------------------------------- Aufsetzen */
  function mount(scope) {
    var roots = (scope || document).querySelectorAll('[data-story]');
    Array.prototype.forEach.call(roots, function (root) {
      // Doppelte Instanzen im Theme-Editor verhindern
      if (root.__lafStory) return;
      var s = new Story(root);
      root.__lafStory = s;
      instances.push(s);
    });
  }

  function unmount(scope) {
    var roots = (scope || document).querySelectorAll('[data-story]');
    Array.prototype.forEach.call(roots, function (root) {
      var s = root.__lafStory;
      if (!s) return;
      s.destroy();
      root.__lafStory = null;
      var i = instances.indexOf(s);
      if (i > -1) instances.splice(i, 1);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mount(document); });
  } else {
    mount(document);
  }

  document.addEventListener('shopify:section:load', function (e) { mount(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { unmount(e.target); });
})();
